import { useEffect, useState } from 'react';

import { endMinute, isPast, localDate, wallClock, type Opportunity, type WallClock } from './opportunity';

/**
 * Which events are over.
 *
 * Explore is a list of things to go to, and an event that has already happened
 * is not one of them — it is the first thing on the list for anyone opening
 * the site mid-September, and the least useful. My Schedule is different: it is
 * the record of someone's week, so an event they added stays there after it
 * ends, and says so.
 *
 * "Over" means ended, not started. Someone checking at 6:30 can still walk
 * into the reception that began at six. The backend applies the same rule to
 * what it lists; this is the same rule for what is already on screen.
 */

/** A moment on New York's clock, in the same terms the events are read in. */
export type Moment = WallClock;

/**
 * Every event is New York time, so "now" has to be as well: a visitor
 * planning from London at 11pm would otherwise see the whole of the next New
 * York evening as already gone.
 */
export function newYorkNow(at: Date = new Date()): Moment {
  return wallClock(at, 'America/New_York');
}

export function isPastEvent(event: Opportunity, now: Moment = newYorkNow()): boolean {
  return isPast(event, now);
}

/** Minutes until the next of these events ends today, or until midnight. */
function minutesUntilNextChange(events: Opportunity[], now: Moment): number {
  let next = 24 * 60 - now.minutes;
  for (const e of events) {
    const end = endMinute(e);
    if (localDate(e) !== now.dayKey || end == null || end <= now.minutes) continue;
    next = Math.min(next, end - now.minutes);
  }
  return next;
}

/**
 * "Now", moved on only when one of these events actually ends.
 *
 * Rather than ticking every minute — which would re-filter Explore sixty times
 * an hour for nothing — it sleeps until the next moment something ends. A phone
 * that was asleep throttles that timer, so coming back to the tab checks again.
 */
export function useNow(events: Opportunity[]): Moment {
  const [now, setNow] = useState<Moment>(() => newYorkNow());

  useEffect(() => {
    let timer = 0;

    const check = () => {
      const next = newYorkNow();
      if (next.dayKey !== now.dayKey || events.some((e) => isPast(e, next) !== isPast(e, now))) {
        setNow(next);
      } else {
        schedule();
      }
    };

    const schedule = () => {
      const current = newYorkNow();
      const wait = minutesUntilNextChange(events, current) * 60_000 - (Date.now() % 60_000);
      // A beat past the minute, so the check lands after the end rather than on it.
      timer = window.setTimeout(check, Math.max(wait, 0) + 500);
    };

    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      window.clearTimeout(timer);
      check();
    };

    schedule();
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [events, now]);

  return now;
}
